import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import LoggingService from 'components/core/logging/LoggingService';
import { TOAST_PLACEMENTS, type ToastPlacement } from 'components/ui/shared/Toast/Toast';
import ToastRegion, { type ActiveNotification } from 'components/ui/shared/Toast/ToastRegion';
import { MAX_STACKED_TOASTS } from 'components/ui/shared/Toast/useToastStack';
import {
  NotificationContext,
  type NotificationInput,
  type ShowErrorNotificationOptions,
} from 'contexts/NotificationContext';
import { takeNavigationRequestedAt } from 'providers/navigationPending';

import { getErrorNotificationContent } from 'store/api/graph/interfaces/apiErrors';

import { SPACE_400 } from 'styles/tokens';

const MessageList = styled.ul`
  margin: 0;
  padding-left: ${SPACE_400};
`;

const DEFAULT_TOAST_PLACEMENT: ToastPlacement = 'top-center';

const REMOVAL_FALLBACK_MS = 500;

const NAVIGATION_GRACE_MS = 500;

interface Props {
  children: ReactNode;
}

type SettledLocation = {
  /**
   * The toasts on screen as of that location, which is what a navigation retires.
   */
  ids: number[];
  pathname: string;
};

type TrackedNotification = ActiveNotification & {
  raisedAt: number;
};

const supersede = (notifications: TrackedNotification[], groupKey: string | undefined) =>
  groupKey === undefined
    ? notifications
    : notifications.map(notification =>
        notification.groupKey === groupKey && notification.open ? { ...notification, open: false } : notification
      );

const capOpenNotifications = (notifications: TrackedNotification[]) => {
  let excess = notifications.filter(({ open }) => open).length - MAX_STACKED_TOASTS;

  if (excess <= 0) {
    return notifications;
  }

  return notifications.map(notification => {
    if (!notification.open || excess <= 0) {
      return notification;
    }

    excess -= 1;

    return { ...notification, open: false };
  });
};

const isSamePage = (from: string, to: string) => {
  const fromSegments = from.split('/').filter(Boolean);
  const toSegments = to.split('/').filter(Boolean);
  const shared = Math.min(fromSegments.length, toSegments.length);

  return shared > 0 && fromSegments.slice(0, shared).every((segment, index) => segment === toSegments[index]);
};

const retireOnNavigation = (notifications: TrackedNotification[], staleIds: Set<number>, settledBefore: number) => {
  const isStale = ({ id, open, raisedAt }: TrackedNotification) =>
    open && staleIds.has(id) && raisedAt <= settledBefore;

  return notifications.some(isStale)
    ? notifications.map(notification => (isStale(notification) ? { ...notification, open: false } : notification))
    : notifications;
};

export const NotificationProvider = ({ children }: Props) => {
  const [notifications, setNotifications] = useState<TrackedNotification[]>([]);
  const lastId = useRef(0);
  const removalTimers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const [isTabUnreachable, setIsTabUnreachable] = useState(false);
  const hiddenEpoch = useRef(0);
  const { pathname } = useLocation();

  const settled = useRef<SettledLocation>({ ids: [], pathname });

  /*
   * A backgrounded tab freezes `requestAnimationFrame`, so a toast raised in one never flips to its
   * mounted state — it counts down at opacity 0 and is gone before the tab is looked at again. Radix
   * held its own countdown on window blur for this reason; that timer is off here, so the hold is re-made.
   *
   * Focus alone misses the common case: Chromium fires `blur` when the window loses focus, not when the
   * reader switches to a sibling tab — hence `document.hidden` read alongside it, and a sync on mount,
   * since a tab opened in the background starts hidden.
   */
  useEffect(() => {
    const sync = () => {
      if (document.hidden) {
        hiddenEpoch.current += 1;
      }

      setIsTabUnreachable(document.hidden || !document.hasFocus());
    };

    sync();
    window.addEventListener('blur', sync);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);

    return () => {
      window.removeEventListener('blur', sync);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  const removeNotification = useCallback((id: number) => {
    setNotifications(previous => previous.filter(notification => notification.id !== id));
  }, []);

  const closeNotification = useCallback((id: number) => {
    setNotifications(previous =>
      previous.map(notification => (notification.id === id ? { ...notification, open: false } : notification))
    );
  }, []);

  useEffect(() => {
    const { ids, pathname: settledPathname } = settled.current;

    /*
     * Read on every navigation and not just a leaving one, so a view opening over its page cannot leave
     * its timestamp behind for whichever navigation eventually does leave.
     */
    if (isSamePage(settledPathname, pathname)) {
      return;
    }

    const requestedAt = takeNavigationRequestedAt() ?? Date.now();
    const staleIds = new Set(ids);
    const settledBefore = requestedAt - NAVIGATION_GRACE_MS;

    setNotifications(previous => retireOnNavigation(previous, staleIds, settledBefore));
  }, [pathname]);

  useEffect(() => {
    settled.current = { ids: notifications.map(({ id }) => id), pathname };
  }, [notifications, pathname]);

  useEffect(() => {
    const timers = removalTimers.current;
    const liveIds = new Set(notifications.map(({ id }) => id));

    for (const [id, timer] of timers) {
      if (liveIds.has(id)) {
        continue;
      }

      clearTimeout(timer);
      timers.delete(id);
    }

    for (const { id, open } of notifications) {
      if (open || timers.has(id)) {
        continue;
      }

      const armedEpoch = hiddenEpoch.current;
      const armedHidden = document.hidden;

      timers.set(
        id,
        setTimeout(() => {
          timers.delete(id);

          const wasEverHidden = armedHidden || document.hidden || hiddenEpoch.current !== armedEpoch;

          if (!wasEverHidden && !window.matchMedia('(prefers-reduced-motion)').matches) {
            LoggingService.warn({
              message: 'Toast removed by fallback timer; its exit transition never ended',
              messageContext: { id },
            });
          }

          setNotifications(previous => previous.filter(notification => notification.id !== id));
        }, REMOVAL_FALLBACK_MS)
      );
    }
  }, [notifications]);

  useEffect(() => {
    const timers = removalTimers.current;

    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, []);

  const showNotification = useCallback((input: NotificationInput | string) => {
    const normalized: NotificationInput = typeof input === 'string' ? { children: input } : input;
    lastId.current += 1;
    const id = lastId.current;

    /*
     * Appended last, never prepended: Radix derives its F8 and Tab order by reversing DOM order, so the
     * newest toast has to be the last child for keyboard traversal to start at it.
     */
    setNotifications(previous =>
      capOpenNotifications([
        ...supersede(previous, normalized.groupKey),
        {
          action: normalized.action,
          children: normalized.children,
          duration: normalized.duration,
          groupKey: normalized.groupKey,
          icon: normalized.icon,
          id,
          open: true,
          placement: normalized.placement ?? DEFAULT_TOAST_PLACEMENT,
          raisedAt: Date.now(),
          title: normalized.title,
          variant: normalized.variant,
        },
      ])
    );
  }, []);

  const showErrorNotification = useCallback(
    (error: unknown, options?: ShowErrorNotificationOptions) => {
      const { messages } = getErrorNotificationContent(error);
      const body =
        messages.length === 1 ? (
          messages[0]
        ) : (
          <MessageList>
            {messages.map(message => (
              <li key={message}>{message}</li>
            ))}
          </MessageList>
        );
      showNotification({
        children: body,
        groupKey: options?.groupKey,
        icon: options?.icon,
        placement: options?.placement,
        title: options?.title,
      });
    },
    [showNotification]
  );

  const contextValue = useMemo(
    () => ({ showErrorNotification, showNotification }),
    [showErrorNotification, showNotification]
  );

  /*
   * Only the placements with something to show get a region: an empty viewport is still a fixed anchor in
   * the DOM and another F8 target for the screen reader.
   */
  const regions = useMemo(
    () =>
      TOAST_PLACEMENTS.map(placement => ({
        placed: notifications.filter(notification => notification.placement === placement),
        placement,
      })).filter(({ placed }) => placed.length > 0),
    [notifications]
  );

  return (
    <NotificationContext value={contextValue}>
      {children}
      {regions.map(({ placed, placement }) => (
        <ToastRegion
          isTabUnreachable={isTabUnreachable}
          key={placement}
          notifications={placed}
          onClose={closeNotification}
          onRemove={removeNotification}
          placement={placement}
        />
      ))}
    </NotificationContext>
  );
};
